import { createAsyncThunk } from '@reduxjs/toolkit';

import { APIBaseResponse } from '..';
import { handleListFetch } from '../apiHelpers';
import { APISuiteGet } from '../ApplicationSuites/ApplicationSuites.api';
import { APITenantGetResponse } from '../Tenants/TenantApiTypeDefs';
import { APITenantGet } from '../Tenants/Tenants.api';

import {
    APIRoleDelete,
    APIRoleGet,
    APIRoleGetList,
    APIRolePost,
    APIRolePostParams,
    APIRolePostResponse,
    APIRolePut,
    APIRolePutParams,
    Role
} from './Roles.api';

export const fetchRoles = createAsyncThunk('roles/fetchRoles', async (params: { tenantId: string, signal?: AbortSignal }) => {
    const response = await APIRoleGetList(params);
    return response.data;
});

export type PostRoleThunkResponse = {
    item: APIRolePostParams,
    response: APIRolePostResponse,
    rows: Role[]
}

export type PutRoleThunkResponse = {
    item: APIRolePutParams,
    response: APIBaseResponse,
    rows: Role[]
}

export const postRole = createAsyncThunk('roles/postRole', async (item: APIRolePostParams) => {
    const response = await APIRolePost(item);
    const rows = await handleListFetch(APIRoleGetList({ tenantId: item.tenantId }));
    return {
        item,
        response,
        rows,
    };
});

export const deleteRole = createAsyncThunk('roles/deleteRole', async (item: {tenantId: string, id: string}) => {
    const response = await APIRoleDelete(item);
    const rows = await handleListFetch(APIRoleGetList({ tenantId: item.tenantId }));
    return {
        item,
        response,
        rows
    };
});

type FetchRoleDetailsParams = {
    id: string;
    tenantId: string;
    applicationSuiteId: string;
}

export type FetchRoleDetailsResponse = {
    applicationSuiteId: string,
    applicationSuiteName: string,
    tenantId: string,
    tenantName: string,
    tenantRequiresLicense: boolean,
    id: string,
    name: string
}

export const fetchRoleDetails = createAsyncThunk('roleDetails/fetchDetails', async (fetchParams: FetchRoleDetailsParams): Promise<FetchRoleDetailsResponse> => {
    const requests = [
        APISuiteGet({ id: fetchParams.applicationSuiteId }),
        APITenantGet({ id: fetchParams.tenantId }),
        APIRoleGet({ tenantId: fetchParams.tenantId, id: fetchParams.id })
    ];
    const responses = await Promise.all(requests);
    const tenantResponse = responses[1] as APITenantGetResponse;
    return {
        applicationSuiteId: responses[0].data.id,
        applicationSuiteName: responses[0].data.name,
        tenantId: tenantResponse.data.id,
        tenantName: tenantResponse.data.name,
        tenantRequiresLicense: tenantResponse.data.isLicenseRequired,
        id: responses[2].data.id,
        name: responses[2].data.name
    };
});

export const putRole = createAsyncThunk('roles/putRole', async (item: APIRolePutParams) => {
    const response = await APIRolePut(item);
    const rows = await handleListFetch(APIRoleGetList({ tenantId: item.tenantId }));
    return {
        response,
        item,
        rows,
    };
});
